import { Alert, Box, Button, Card, CircularProgress, Dialog, Grid, Snackbar, Stack, TextField, Typography, useTheme, alpha, Container, InputAdornment } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import CenteredBox from '../../components/centeredBox/centeredBox'
import { clearUserState, forgotPassword } from '../../state/slices/user'
import { Email, LockReset, ArrowBack } from '@mui/icons-material'

function ForgotPassword() {
  let theme = useTheme()
  let userState = useSelector(state => state.user)
  let navigate = useNavigate()
  let dispatch = useDispatch()
  const { t } = useTranslation()

  let [snackbarOpen, setSnackbarOpen] = useState(false)

  let [submitStatus, setSubmitStatus] = useState('')

  const [formData, setFormData] = useState({
    emailOrUsername: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Perform validation on the individual field as the user types
    validateField(e.target.name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    const validationErrors = { ...errors };

    if (fieldName === 'emailOrUsername') {
      validationErrors.emailOrUsername = value.trim() ? '' : t('validation.emailOrUsernameRequired') || 'Email or username is required';
    }

    setErrors((prev) => { return { ...prev, ...validationErrors } });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = {};

    Object.keys(formData).forEach((fieldName) => {
      validateField(fieldName, formData[fieldName]);
      if (errors[fieldName]) {
        validationErrors[fieldName] = errors[fieldName];
      }
    });

    setSubmitStatus(prev => prev + 'x')

  };

  useEffect(() => {
    if (submitStatus && Object.keys(errors).filter(key => errors[key]).length === 0) {
      dispatch(forgotPassword({ ...formData }));
      console.log('Form submitted:', formData);
      setSnackbarOpen(true)
    } else {
      dispatch(clearUserState())
    }
  }, [submitStatus])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      {!userState.loading && 
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          message={(
            <Typography fontWeight={600} fontSize={16} color='white'>
              {userState.successMsg 
                ? (userState.emailSentTo === 'parent'
                    ? t('forgotPassword.emailSentToParent')
                    : userState.emailSentTo === 'business'
                    ? t('forgotPassword.emailSentToBusiness')
                    : t('forgotPassword.emailSentSuccess'))
                : t('forgotPassword.emailNotFound')}
            </Typography>
          )} 
        />
      }
      <Container maxWidth="lg">
        <Grid container spacing={0} alignItems="stretch">
          {/* Left Image Section */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                minHeight: '600px',
                borderRadius: '4px 0 0 4px',
                overflow: 'hidden',
                boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <img 
                src="/3227472.jpg" 
                alt="Forgot Password" 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }} 
              />
            </Box>
          </Grid>

          {/* Right Form */}
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '0 4px 4px 0',
                p: { xs: 4, md: 5 },
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {/* Logo/Header Section */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2.5,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    mb: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <LockReset sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  {t('forgotPassword.title')}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                  }}
                >
                  {t('forgotPassword.subtitle')}
                </Typography>
              </Box>

              <Stack spacing={3}>
                <TextField
                  label={t('forgotPassword.emailOrUsername') || 'Email or Username'}
                  variant='outlined'
                  name='emailOrUsername'
                  value={formData.emailOrUsername}
                  onChange={handleChange}
                  error={!!errors.emailOrUsername}
                  helperText={errors.emailOrUsername || (t('forgotPassword.emailOrUsernameHelper') || 'Enter your email address or username')}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        }
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                      }
                    }
                  }}
                />

                {userState.errorMsg && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: 3,
                      bgcolor: alpha('#d32f2f', 0.1),
                    }}
                  >
                    {userState.errorMsg}
                  </Alert>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={userState.loading}
                  fullWidth
                  size="large"
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.secondary.main,
                    textTransform: 'capitalize',
                    py: 1.75,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                      background: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.primary.main, 0.5),
                      transform: 'none',
                    }
                  }}
                  disableElevation
                  variant='contained'
                >
                  {userState.loading ? (
                    <CircularProgress size={25} color="inherit" />
                  ) : (
                    <>
                      <LockReset sx={{ mr: 1.5, fontSize: 22 }} />
                      {t('forgotPassword.submit')}
                    </>
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Link
                    to="/login"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      textDecoration: 'none',
                      gap: '4px',
                    }}
                  >
                    <ArrowBack sx={{ fontSize: 18 }} />
                    {t('forgotPassword.backToLogin')}
                  </Link>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Dialog
        open={!!userState.successMsg}
        onClose={() => navigate('/login')}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 3,
          }
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.success.main, 0.1),
              mb: 2,
            }}
          >
            <Email sx={{ fontSize: 40, color: theme.palette.success.main }} />
          </Box>
          <Typography variant="h5" color="primary" fontWeight={700} sx={{ mb: 2 }}>
            {userState.emailSentTo === 'parent' 
              ? t('forgotPassword.emailSentToParent')
              : userState.emailSentTo === 'business'
              ? t('forgotPassword.emailSentToBusiness')
              : t('forgotPassword.emailSentSuccess')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {userState.emailSentTo === 'parent'
              ? t('forgotPassword.emailSentToParentInstructions')
              : userState.emailSentTo === 'business'
              ? t('forgotPassword.emailSentToBusinessInstructions')
              : t('forgotPassword.emailInstructions')}
          </Typography>
          <Button 
            onClick={() => navigate('/login')} 
            variant="contained" 
            fullWidth
            sx={{
              background: theme.palette.primary.main,
              color: theme.palette.secondary.main,
              fontWeight: 700,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                background: theme.palette.primary.dark,
              }
            }}
            disableElevation
          >
            {t('forgotPassword.ok')}
          </Button>
        </Box>
      </Dialog>
    </Box>
  )
}

export default ForgotPassword


